import React, { memo } from 'react';
import {
  TableCell, TableRow, IconButton, Tooltip, Chip
} from '@mui/material';
import {
  CheckCircle, Cancel, Send, Visibility, Delete
} from '@mui/icons-material';
import StatusChip from './StatusChip';

/**
 * Optimized Transfer Row Component
 * Uses React.memo to prevent unnecessary re-renders
 * Implements stable key and optimized prop comparison
 */

const TransferRow = memo(({ 
  transfer, 
  user, 
  onAction,
  showDeleted,
  deleteLoading,
  actionLoading 
}) => {
  // Early return for deleted transfers if not showing
  if (transfer.isDeleted && !showDeleted) {
    return null;
  }

  // Determine action permissions
  const isMaker = user?.role === 'MAKER';
  const canApprove = ['CHECKER', 'ADMIN'].includes(user?.role);
  const isCreator = transfer.createdBy?._id === user?._id;

  // Handle action clicks
  const handleAction = (actionType, transferId) => {
    if (onAction) {
      onAction(actionType, transferId);
    }
  };

  return (
    <TableRow 
      key={transfer._id}
      sx={{ 
        '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
        opacity: transfer.isDeleted ? 0.6 : 1
      }}
    >
      <TableCell sx={{ fontSize: { xs: 11, sm: 12 } }}>
        {new Date(transfer.createdAt).toLocaleDateString('en-IN', {
          day: '2-digit',
          month: 'short',
          year: 'numeric'
        })}
      </TableCell>
      
      <TableCell sx={{ fontSize: { xs: 11, sm: 12 } }}>
        <div>
          <div>{transfer.fromInvestor?.fullName}</div>
          <div style={{ fontSize: '0.8em', color: '#666' }}>
            {transfer.fromInvestor?.folioNumber}
          </div>
        </div>
        <div style={{ margin: '4px 0' }}></div>
        <div>
          <div>{transfer.toInvestor?.fullName}</div>
          <div style={{ fontSize: '0.8em', color: '#666' }}>
            {transfer.toInvestor?.folioNumber}
          </div>
        </div>
      </TableCell>
      
      <TableCell sx={{ fontSize: { xs: 11, sm: 12 } }}>
        <div>
          <div>{transfer.security?.companyName}</div>
          <div style={{ fontSize: '0.8em', color: '#666' }}>
            {transfer.security?.isin}
          </div>
        </div>
      </TableCell>
      
      <TableCell align="right" sx={{ fontSize: { xs: 11, sm: 12 } }}>
        {transfer.quantity?.toLocaleString()}
      </TableCell>
      
      <TableCell align="center" sx={{ fontSize: { xs: 11, sm: 12 } }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
          <div>
            {transfer.beforeFromShares} 
            {transfer.afterFromShares !== null && (
              <span style={{ color: 'red', marginLeft: '4px' }}>
                {'>'} {transfer.afterFromShares}
              </span>
            )}
          </div>
          <Chip 
            size="small" 
            label={transfer.lockedShares > 0 ? `${transfer.lockedShares} Locked` : 'No Lock'} 
            color={transfer.lockedShares > 0 ? 'warning' : 'default'}
            variant="outlined"
          />
        </div>
      </TableCell>
      
      <TableCell align="center" sx={{ fontSize: { xs: 11, sm: 12 } }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
          <div>
            {transfer.beforeToShares}
            {transfer.afterToShares !== null && (
              <span style={{ color: 'green', marginLeft: '4px' }}>
                {'>'} {transfer.afterToShares}
              </span>
            )}
          </div>
          {transfer.lockedQuantity > 0 && (
            <Chip 
              size="small" 
              label={`${transfer.lockedQuantity} Pending`} 
              color="info"
              variant="outlined"
            />
          )}
        </div>
      </TableCell>
      
      <TableCell align="center" sx={{ fontSize: { xs: 11, sm: 12 } }}>
        <StatusChip status={transfer.status} />
      </TableCell>
      
      <TableCell align="center">
        <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
          {/* Delete button - Admin only for initiated transfers */}
          {!transfer.isDeleted && user?.role === 'ADMIN' && ['INITIATED'].includes(transfer.status) && (
            <Tooltip title="Delete">
              <IconButton 
                size="small" 
                color="error" 
                disabled={deleteLoading}
                onClick={() => handleAction('delete', transfer._id)}
              >
                <Delete fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          
          {/* View details */}
          <Tooltip title="View Details">
            <IconButton 
              size="small" 
              onClick={() => handleAction('view', transfer._id)}
            >
              <Visibility fontSize="small" />
            </IconButton>
          </Tooltip>
          
          {/* Submit for review - Maker/Admin for own initiated transfers */}
          {(isMaker || user?.role === 'ADMIN') && transfer.status === 'INITIATED' && isCreator && !transfer.isDeleted && (
            <Tooltip title="Submit for Review">
              <IconButton 
                size="small" 
                color="primary"
                onClick={() => handleAction('submit', transfer._id)}
                disabled={actionLoading}
              >
                <Send fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          
          {/* Approve/Reject - Checker/Admin for under review transfers */}
          {canApprove && transfer.status === 'UNDER_REVIEW' && !transfer.isDeleted && (
            <>
              <Tooltip title="Approve & Execute">
                <IconButton 
                  size="small" 
                  color="success"
                  onClick={() => handleAction('approve', transfer._id)}
                  disabled={actionLoading}
                >
                  <CheckCircle fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Reject">
                <IconButton 
                  size="small" 
                  color="error"
                  onClick={() => handleAction('reject', transfer._id)}
                  disabled={actionLoading}
                >
                  <Cancel fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
});

// Custom comparison function for optimal performance
TransferRow.displayName = 'TransferRow';

export default TransferRow;
